import {
  PLAYER_H, PLAYER_W, TICK_RATE, WORLD_H, WORLD_W,
  type CrateState, type GameEvent, type InputState, type LevelDef, type Platform,
  type PlayerState, type Point, type Rect, type RoomSnapshot, type WorldState,
} from '../shared/types';
import { PHYSICS, getPlatforms, isGateOpen, isHazardActive } from '../shared/simulation';
import { getAudioDiagnostics, playGameEvent, setAudioTheme } from './audio';
import { drawHero } from './art/hero';
import { TrailCamera, toScreen, type CameraView } from './art/camera';
import { TrailAnnotations, type AnnotationRecord, type LabelStats } from './art/annotations';
import { MovementReplay, type TrajectorySample } from './art/movement';
import { RemoteTimeline, SnapshotClock, type RemoteSample } from './art/timeline';
import { ScenePlacer, type PlacedBody, type PlacedScene } from './art/placement';
import { CooperativeFocus } from './art/cooperation';
import { describeHazard, drawProtectionProjectors } from './art/hazards';
import { LANTERN_COLORS, drawLantern } from './art/lantern';
import { AMBER, INK, PAPER, TAU, clamp, disc, hash, line, mix, pill, random, rounded, spark } from './art/paint';
import {
  RESET_BEAT_MS, RESET_FADE_MS, RESET_SHAKE_MS, attributeRequestedReset, attributeReset, drawFallMark, drawResetChip, measureResetChip,
  resetLabel, resetSentence, type ResetBeat, type ResetChipLayout,
} from './art/resets';
import {
  PALETTES, drawBackdrop, drawCircuits, drawCrate, drawDoor, drawDormantBridge,
  drawGate, drawHazard, drawKey, drawPlatform, drawSwitch,
} from './art/world';

export interface RendererProps {
  snapshot: RoomSnapshot | null;
  playerId: string | null;
  input: { current: InputState };
  sound: boolean;
  reducedMotion?: boolean;
  mode?: 'game' | 'hero';
  /** Internal presentation option; GameCanvas's public API remains unchanged. */
  view?: 'follow' | 'overview';
  /** Reveal the authored in-world sign cards. Off by default; the shell's hint toggle owns it. */
  showHints?: boolean;
}

interface VisualPlayer extends PlayerState {
  anticipation: number;
  path: PlacedBody['path'];
  interpolated: Point;
  offset: Point;
}

interface RenderScene {
  level: LevelDef;
  world: WorldState;
  remote: RemoteSample;
  trajectory: TrajectorySample | null;
  placed: PlacedScene;
  platforms: Platform[];
  players: VisualPlayer[];
  crates: CrateState[];
}

interface LocalVisual {
  id: string;
  offsetX: number;
  direction: number;
  directionAt: number;
  jumping: boolean;
  jumpAt: number;
  jumpY: number;
  jumpSequence: number;
  jumpAckSequence: number | null;
  jumpAckAt: number | null;
  jumpEventFloor: number;
}

interface Particle {
  x: number; y: number; vx: number; vy: number; gravity: number;
  size: number; born: number; life: number; color: string; spin: number;
  kind: 'dust' | 'glitter' | 'confetti' | 'droplet' | 'ember' | 'smoke';
}

interface Ring { x: number; y: number; born: number; life: number; color: string }
interface KeyFlight { from: Point; to: Point; born: number; life: number }
interface QueuedEvent { event: GameEvent; when: number; key: string }

const TICK_MS = 1000 / TICK_RATE;
/** Celebration poses/glow after a received `complete`; longer than the shell's ~1.6 s hold. */
const CELEBRATION_MS = 2600;
/** Events of other couriers' bodies (jump, land) sound where their delayed body is drawn. */
const KINEMATIC_EVENTS = new Set<GameEvent['type']>(['jump', 'land']);
let rendererSequence = 0;
const renderers = new Set<HearthhopRenderer>();

/** Read-only instrumentation. It deliberately exposes neither room codes nor resume credentials. */
export interface RenderDiagnostics {
  readonly rendererId: number;
  readonly mode: 'game' | 'hero';
  readonly scene: 'original illustration' | 'authoritative session' | 'awaiting snapshot';
  readonly frames: number;
  readonly fps: number;
  readonly renderMs: number;
  readonly tick: number | null;
  readonly epoch: number | null;
  readonly level: number | null;
  readonly theme: string;
  readonly viewport: Readonly<{ width: number; height: number; dpr: number; backingWidth: number; backingHeight: number; worldScale: number }>;
  readonly world: Readonly<{ width: number; height: number; playerWidth: number; playerHeight: number }>;
  readonly interpolation: Readonly<{ delayMs: number; bufferedSnapshots: number; amount: number; staleSnapshots: number; renderTick: number; holds: number }>;
  readonly localPrediction: Readonly<{ x: number; y: number; inputSequence: number; acknowledgedSequence: number; cosmeticOnly: boolean }>;
  readonly prediction: Readonly<ReturnType<MovementReplay['diagnostics']> & ReturnType<ScenePlacer['diagnostics']>>;
  /** Both render clocks in authoritative ticks: the local courier leads, remote bodies trail. */
  readonly renderClock: Readonly<{ authorityTick: number | null; estimatedTick: number | null; localTick: number | null; remoteTick: number | null; leadTicks: number; delayTicks: number; localRate: number; remoteRate: number; localResyncs: number; remoteResyncs: number; clockOffsetMs: number | null }>;
  readonly cooperation: Readonly<ReturnType<CooperativeFocus['diagnostics']>>;
  readonly players: ReadonlyArray<Readonly<{ id: string; number: number; x: number; y: number; authorityX: number; authorityY: number; interpolatedX: number; interpolatedY: number; offsetX: number; offsetY: number; path: PlacedBody['path']; grounded: boolean; supportId: string | null; entered: boolean; connected: boolean }>>;
  readonly platforms: ReadonlyArray<Readonly<Rect & { id: string }>>;
  readonly gates: ReadonlyArray<Readonly<{ id: string; open: boolean }>>;
  readonly hazards: ReadonlyArray<Readonly<{ id: string; active: boolean; label: string; keyLocked: boolean; held: boolean; seconds: number | null }>>;
  readonly switches: ReadonlyArray<Readonly<{ id: string; count: number; required: number; active: boolean }>>;
  readonly events: Readonly<{ lastId: number; consumed: number; queued: number; particles: number; keyArcs: number }>;
  /** Presentation state of a received `complete`: age since the event was consumed by THIS renderer. */
  readonly celebration: Readonly<{ ageMs: number | null; active: boolean; durationMs: number }>;
  /** The attributed reset beat (who fell, where, how it was inferred) and the chip drawn for it. */
  readonly reset: Readonly<{ active: boolean; ageMs: number | null; durationMs: number; eventId: number | null; kind: 'fall' | 'retry' | null;
    playerId: string | null; name: string | null; color: number | null; surface: string | null; point: Readonly<Point> | null;
    hazardId: string | null; basis: string | null; ticksAhead: number | null; label: string | null; chip: Readonly<Rect> | null }>;
  /** The persistent counter chip: authoritative `world.deaths` (room-cumulative) and its slot. */
  readonly retries: Readonly<{ deaths: number | null; label: string | null; chip: Readonly<Rect> | null }>;
  readonly reducedMotion: boolean;
  readonly camera: Readonly<CameraView> | null;
  readonly annotations: ReadonlyArray<Readonly<AnnotationRecord>>;
  /** Per-frame label accounting: dense frame (badges on bodies, collapsed chips, no leaders, a callout budget), counts, drops. */
  readonly labels: Readonly<LabelStats>;
  /** The trail map's screen rectangle this frame, or null when the annotation layer found no clear slot. */
  readonly trailMap: Readonly<Rect> | null;
  readonly sampling: Readonly<{ clock: 'shared' | 'movement-replay'; fromTick: number; toTick: number; horizontalOffset: number; cornerTransitions: number; cornerCorrection: number }>;
  readonly jumpFeedback: Readonly<{ requestedAt: number | null; ageMs: number | null; strength: number; inputAcknowledged: boolean }>;
  readonly audio: ReturnType<typeof getAudioDiagnostics>;
}

declare global {
  interface Window { readonly __hearthhopRender?: RenderDiagnostics }
}

function exposeDiagnostics(): void {
  if (typeof window === 'undefined') return;
  const existing = Object.getOwnPropertyDescriptor(window, '__hearthhopRender');
  if (existing && !existing.configurable) return;
  Object.defineProperty(window, '__hearthhopRender', {
    configurable: true,
    enumerable: false,
    get: () => {
      const all = [...renderers];
      const renderer = [...all].reverse().find(r => r.mode === 'game') ?? all.at(-1);
      return renderer?.getDiagnostics();
    },
  });
}

function cloneWorld(world: WorldState): WorldState {
  // Socket payloads are normally immutable. Copying the small changing arrays also makes
  // a simulation-backed preview safe: presentation can never mutate an authority's state.
  return {
    ...world,
    players: world.players.map(player => ({ ...player })),
    crates: world.crates.map(crate => ({ ...crate })),
    switches: world.switches.map(plate => ({ ...plate })),
    collectedKeys: [...world.collectedKeys],
    events: world.events.map(event => ({ ...event })),
  };
}

function overlapX(a: Rect, b: Rect): boolean { return a.x < b.x + b.w && a.x + a.w > b.x; }

const INPUT_EVENTS = ['keydown', 'keyup', 'pointerdown', 'pointerup', 'pointercancel', 'blur'] as const;

/** A persistent rAF renderer. It never calls stepWorld, changes input, or sends network messages. */
export class HearthhopRenderer {
  readonly id = ++rendererSequence;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly resizeObserver: ResizeObserver | null;
  private readonly intersectionObserver: IntersectionObserver | null;
  private props: RendererProps;
  private raf = 0;
  private destroyed = false;
  private inView = true;
  private cssWidth = 0;
  private cssHeight = 0;
  private dpr = 1;
  private worldScale = 1;
  private background: HTMLCanvasElement | null = null;
  private backgroundKey = '';
  private staticHeroDrawn = false;
  private lastSnapshot: RoomSnapshot | null = null;
  private lastReceivedSnapshot: RoomSnapshot | null = null;
  private authority: WorldState | null = null;
  private room = '';
  private epoch = -1;
  private levelId = -1;
  private revision = -1;
  private snapshotClock = new SnapshotClock();
  private remote = new RemoteTimeline();
  private movement = new MovementReplay();
  private placer = new ScenePlacer();
  private previousLocalTick = NaN;
  private local: LocalVisual | null = null;
  private cooperation = new CooperativeFocus();
  private lastFrameAt = 0;
  private lastEventId = 0;
  private eventCount = 0;
  private staleSnapshots = 0;
  private events: QueuedEvent[] = [];
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private flights: KeyFlight[] = [];
  private landings = new Map<string, number>();
  private arrivals = new Map<string, number>();
  private emotes = new Map<string, { at: number; text: string }>();
  private celebrationAt = -Infinity;
  private resetAt = -Infinity;
  private resetBeat: ResetBeat | null = null;
  private resetChip: ResetChipLayout | null = null;
  private retriesChip: Rect | null = null;
  private drawCount = 0;
  private fps = 0;
  private fpsAt = 0;
  private fpsFrames = 0;
  private renderMs = 0;
  private visualPlayers: VisualPlayer[] = [];
  private visualPlatforms: Platform[] = [];
  private camera = new TrailCamera();
  private cameraView: CameraView | null = null;
  private annotations = new TrailAnnotations();
  private scene: RenderScene | null = null;
  private cornerTransitions = 0;
  private cornerCorrection = 0;

  constructor(private readonly canvas: HTMLCanvasElement, props: RendererProps) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Hearthhop requires a browser with Canvas2D support.');
    this.ctx = ctx;
    this.props = props;
    this.resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(this.resize) : null;
    this.resizeObserver?.observe(canvas);
    this.intersectionObserver = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
      this.inView = entries[0]?.isIntersecting ?? true;
      if (this.inView) this.staticHeroDrawn = false;
    }) : null;
    this.intersectionObserver?.observe(canvas);
    window.addEventListener('resize', this.resize, { passive: true });
    // Passive observation only: the shell/network hook owns the input ref and its
    // transmission. Stamping a change at event time (rather than at the next rAF) is what
    // lets the replay apply it at the tick the service actually applies it.
    for (const type of INPUT_EVENTS) window.addEventListener(type, this.observeInput, { passive: true });
    renderers.add(this);
    exposeDiagnostics();
    this.resize();
    this.update(props);
    this.raf = requestAnimationFrame(this.frame);
  }

  get mode(): 'game' | 'hero' { return this.props.mode ?? 'game'; }

  /** The reset beat in progress, for the shell-side live region ("Bo fell in — everyone back!"). */
  get activeReset(): { eventId: number; kind: 'fall' | 'retry'; label: string } | null {
    const beat = this.resetBeat;
    if (!beat || performance.now() - beat.at >= RESET_BEAT_MS) return null;
    return { eventId: beat.eventId, kind: beat.attribution.kind, label: resetLabel(beat) };
  }

  private readonly observeInput = (): void => {
    if (this.destroyed || this.mode !== 'game') return;
    this.movement.observe(this.props.input.current, performance.now(), true);
  };

  update(props: RendererProps): void {
    if (this.destroyed) return;
    if (props.reducedMotion !== this.props.reducedMotion || props.mode !== this.props.mode) this.staticHeroDrawn = false;
    if (props.reducedMotion) {
      this.particles = [];
      this.rings = [];
      this.flights = [];
    }
    if (props.playerId !== this.props.playerId) this.movement.reset('local-identity', props.input.current);
    this.props = props;
    if (!props.snapshot && this.lastReceivedSnapshot) {
      this.clearScene();
      this.lastSnapshot = null;
      this.lastReceivedSnapshot = null;
      this.movement.reset('snapshot-cleared', props.input.current);
    }
    if (props.snapshot && props.snapshot !== this.lastReceivedSnapshot) {
      this.lastReceivedSnapshot = props.snapshot;
      this.acceptSnapshot(props.snapshot, performance.now());
    }
  }

  private clearScene(): void {
    this.remote.reset();
    this.snapshotClock.reset();
    this.placer.reset();
    this.previousLocalTick = NaN;
    this.authority = null;
    this.events = [];
    this.local = null;
    this.visualPlayers = [];
    this.visualPlatforms = [];
    this.cooperation.reset();
  }

  private acceptSnapshot(snapshot: RoomSnapshot, now: number): void {
    this.movement.observe(this.props.input.current, now);
    const world = snapshot.world;
    const roomChanged = this.room !== snapshot.code;
    if (!roomChanged && snapshot.revision < this.revision) { this.staleSnapshots++; this.movement.reset('snapshot-rollback', this.props.input.current); return; }
    if (!world || !snapshot.level) {
      this.lastSnapshot = snapshot;
      this.room = snapshot.code;
      this.revision = snapshot.revision;
      this.clearScene();
      this.particles = [];
      this.rings = [];
      this.flights = [];
      this.resetBeat = null;
      this.movement.reset('no-world', this.props.input.current);
      return;
    }
    const previous = this.authority;
    const previousLevel = this.lastSnapshot?.level ?? null;
    const sceneChanged = roomChanged || this.epoch !== world.epoch || this.levelId !== snapshot.level.id;
    if (!sceneChanged && previous && world.tick < previous.tick) { this.staleSnapshots++; this.movement.reset('tick-rollback', this.props.input.current); return; }
    // A new epoch of the SAME trail carrying a new `reset` event is a team reset. A requested retry
    // names its courier in the event; a fall does not, so that courier is inferred from the last
    // pre-reset snapshot while it is still at hand (the scene is cleared just below).
    const resetEvent = sceneChanged && !roomChanged && previous && previousLevel && previousLevel.id === snapshot.level.id && world.epoch > previous.epoch
      ? world.events.find(event => event.type === 'reset' && event.id > this.lastEventId) ?? null : null;
    this.lastSnapshot = snapshot;
    this.revision = snapshot.revision;
    this.room = snapshot.code;
    this.epoch = world.epoch;
    this.levelId = snapshot.level.id;
    setAudioTheme(snapshot.level.theme);

    const copy = cloneWorld(world);
    if (sceneChanged || !previous) {
      this.clearScene();
      this.particles = [];
      this.rings = [];
      this.flights = [];
      this.landings.clear();
      this.arrivals.clear();
      this.emotes.clear();
      this.celebrationAt = -Infinity;
      this.movement.reset('scene', this.props.input.current);
      this.camera.reset();
      this.annotations.reset();
      if (resetEvent && previous && previousLevel) this.beginResetBeat(previous, previousLevel, resetEvent, world, now);
      else this.resetBeat = null;
      // Prime a newly mounted/rejoined renderer rather than replaying 64 historic sounds.
      // A real reset retains its event sequence; a newly created level may start from 1.
      const resetSequence = roomChanged || !previous || world.nextEventId <= this.lastEventId;
      if (resetSequence) {
        this.lastEventId = Math.max(0, ...world.events.map(e => e.id));
        const arrivalEvent = [...world.events].reverse().find(e => world.complete ? e.type === 'complete' : e.type === 'start' && world.tick - e.tick <= 8);
        if (arrivalEvent) this.queueEvent(arrivalEvent, snapshot, now, true);
      }
    }
    if (sceneChanged || !previous || world.tick !== previous.tick) this.snapshotClock.observe(world.tick, now);
    this.authority = copy;
    this.remote.push({ world: copy, tick: world.tick, receivedAt: now });
    this.movement.accept(copy, snapshot.level, this.props.playerId, this.props.input.current, now, this.snapshotClock);
    this.cooperation.accept(sceneChanged ? undefined : previous ?? undefined, copy, snapshot.level, now);
    for (const event of world.events) {
      if (event.id <= this.lastEventId) continue;
      this.lastEventId = event.id;
      if (world.tick - event.tick <= TICK_RATE) this.queueEvent(event, snapshot, now);
    }
  }

  private queueEvent(event: GameEvent, snapshot: RoomSnapshot, now: number, immediate = false): void {
    const world = snapshot.world!;
    const key = `${snapshot.code}:${world.levelIndex}:${world.epoch}:${event.id}`;
    if (document.hidden || !this.inView) {
      playGameEvent(event, key, false);
      this.eventCount++;
      return;
    }
    // Events stay authoritative. The local courier's own jump/land was already drawn by
    // the replay, so it sounds at once; another courier's body is drawn at the fixed
    // remote delay, so its jump/land sounds when that body visibly leaves or lands.
    const own = event.playerId === this.props.playerId;
    const delayed = KINEMATIC_EVENTS.has(event.type) && !!event.playerId && !own;
    const lead = delayed ? this.remote.delayTicks * TICK_MS : 0;
    const playout = event.tick * TICK_MS + (this.snapshotClock.ready ? this.snapshotClock.offsetMs : now - event.tick * TICK_MS) + lead;
    const when = !immediate && event.type !== 'reset' ? clamp(playout, now, now + lead + 30) : now;
    this.events.push({ event, when, key });
  }

  /**
   * Start the ~1.1 s beat for a received reset: who asked for the retry (the event's authoritative
   * `playerId`) or who fell (inferred), the splash at the fall location, the fade-in of the freshly
   * reset bodies. The world state itself is already authoritative and reset; this only attributes and
   * presents it.
   */
  private beginResetBeat(previous: WorldState, previousLevel: LevelDef, event: GameEvent, current: WorldState, now: number): void {
    const attribution = attributeRequestedReset(event.playerId, [current, previous]) ?? attributeReset(previous, previousLevel);
    const eventId = event.id;
    this.resetBeat = { at: now, eventId, attribution, sentence: resetSentence(attribution) };
    if (this.props.reducedMotion || attribution.kind !== 'fall' || !attribution.point) return;
    const rng = random(eventId * 7919 + hash(attribution.playerId ?? 'fall'));
    const { x, y } = attribution.point;
    if (attribution.surface === 'water') {
      for (let i = 0; i < 16; i++) {
        this.particles.push({
          x: x + (rng() - 0.5) * 22, y: y + 1, vx: (rng() - 0.5) * 150, vy: -(90 + rng() * 170), gravity: 430,
          size: 1.6 + rng() * 2.2, born: now + rng() * 40, life: 520 + rng() * 260,
          color: i % 3 ? '#e9f7f8' : '#b7dfe2', spin: 0, kind: 'droplet',
        });
      }
    } else if (attribution.surface === 'beam' || attribution.surface === 'bramble') {
      for (let i = 0; i < 14; i++) {
        this.particles.push({
          x: x + (rng() - 0.5) * 18, y: y + (rng() - 0.5) * 18, vx: (rng() - 0.5) * 220, vy: -(20 + rng() * 150), gravity: 160,
          size: 1.4 + rng() * 2, born: now + rng() * 30, life: 380 + rng() * 260,
          color: attribution.surface === 'beam' ? (i % 3 ? '#ffb347' : '#fff1a8') : (i % 2 ? '#c9b08a' : '#7a5a3a'), spin: 0, kind: 'ember',
        });
      }
      for (let i = 0; i < 6; i++) {
        this.particles.push({
          x: x + (rng() - 0.5) * 14, y: y - 4, vx: (rng() - 0.5) * 24, vy: -(26 + rng() * 30), gravity: -12,
          size: 3 + rng() * 3, born: now + 60 + rng() * 120, life: 700 + rng() * 300,
          color: attribution.surface === 'beam' ? '#8d8378' : '#a9a086', spin: 0, kind: 'smoke',
        });
      }
    } else {
      this.burst(x, y - 2, 12, 'dust', now, rng);
    }
  }

  private readonly resize = (): void => {
    if (this.destroyed) return;
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    // Two physical pixels per CSS pixel keeps fine ink crisp without giant mobile backing stores.
    const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    if (this.cssWidth === rect.width && this.cssHeight === rect.height && this.dpr === dpr) return;
    this.cssWidth = rect.width;
    this.cssHeight = rect.height;
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this.staticHeroDrawn = false;
  };

  /** Request cue, facing and exact foot contact on the already placed bodies. */
  private decorate(players: VisualPlayer[], platforms: Platform[], now: number): VisualPlayer[] {
    const localPose = players.find(p => p.id === this.props.playerId);
    if (!localPose || !localPose.connected || localPose.entered || !this.authority) {
      this.local = null;
    } else {
      const input = this.props.input.current;
      const direction = Number(input.right) - Number(input.left);
      if (!this.local || this.local.id !== localPose.id) {
        this.local = { id: localPose.id, offsetX: 0, direction, directionAt: now, jumping: false, jumpAt: -Infinity,
          jumpY: localPose.y, jumpSequence: input.seq, jumpAckSequence: null, jumpAckAt: null, jumpEventFloor: 0 };
      }
      const visual = this.local;
      if (direction !== visual.direction) { visual.directionAt = now; visual.direction = direction; }
      const authority = this.authority;
      const authorityPlayer = authority.players.find(player => player.id === localPose.id);
      if (input.jump && !visual.jumping) {
        // This acknowledges a request, including a buffered request. It does not release
        // support, move a body, play a jump sound, or claim that physics accepted the jump.
        visual.jumpAt = now;
        visual.jumpY = localPose.y;
        visual.jumpSequence = input.seq;
        visual.jumpAckSequence = input.seq > (authorityPlayer?.lastInput ?? 0) ? input.seq : null;
        visual.jumpAckAt = null;
        visual.jumpEventFloor = authority.nextEventId - 1;
      }
      visual.jumping = input.jump;
      // Position now comes from the placed trajectory; the request ornament adds no body offset.
      visual.offsetX = localPose.offset.x;
      if (direction) localPose.facing = direction;
      if (visual.jumpAckSequence === null && input.seq > visual.jumpSequence) visual.jumpAckSequence = input.seq;
      if (visual.jumpAckAt === null && visual.jumpAckSequence !== null && (authorityPlayer?.lastInput ?? -1) >= visual.jumpAckSequence) visual.jumpAckAt = now;
      const age = now - visual.jumpAt;
      const actualJump = authority.events.some(event => event.id > visual.jumpEventFloor && event.type === 'jump' && event.playerId === localPose.id);
      const departed = actualJump && Math.abs(localPose.y - visual.jumpY) > 0.5;
      const expires = Math.min(visual.jumpAt + 600, (visual.jumpAckAt ?? Infinity) + PHYSICS.buffer * 1000);
      const acknowledgedCue = age >= 0 && (age < 125 || (!departed && now < expires));
      localPose.anticipation = acknowledgedCue ? age < 125 ? 1 : 0.68 : 0;
    }
    // Preserve sub-pixel foot contact with the drawn platform geometry and with the actual
    // support IDs, never with a guessed landing.
    for (const player of [...players].sort((p, q) => q.y - p.y)) {
      if (!player.grounded || !player.supportId || player.entered) continue;
      const support = platforms.find(p => p.id === player.supportId) ?? players.find(p => p.id === player.supportId && !p.entered && p.connected);
      if (support && Math.abs(player.y + PLAYER_H - support.y) < 0.75 && overlapX(player, support)) player.y = support.y - PLAYER_H;
    }
    return players;
  }

  private backdrop(theme: LevelDef['theme']): HTMLCanvasElement {
    const key = `${theme}:${this.mode}`;
    if (!this.background || this.backgroundKey !== key) {
      const canvas = document.createElement('canvas');
      canvas.width = WORLD_W * 2;
      canvas.height = WORLD_H * 2;
      const ctx = canvas.getContext('2d', { alpha: false })!;
      ctx.scale(2, 2);
      drawBackdrop(ctx, theme, this.mode === 'hero');
      this.background = canvas;
      this.backgroundKey = key;
    }
    return this.background;
  }

  /**
   * One frame of bodies. Remote-driven bodies: fixed-delay interpolation of received
   * snapshots on a monotonic clock. The local courier and its group: the predicted
   * trajectory sampled between whole ticks on a monotonic clock that leads the authority
   * by the measured acknowledgement lead. Mechanical state and hazard timing stay at the
   * received snapshot; moving platforms are drawn at the local courier's time.
   */
  private sampleScene(level: LevelDef, frameTime: number, dtMs: number): RenderScene | null {
    const authority = this.authority;
    if (!authority) return null;
    const estimated = this.snapshotClock.tickAt(frameTime);
    const remoteTick = this.remote.advance(estimated, dtMs);
    const remote = this.remote.sample(remoteTick);
    if (!remote) return null;
    const localTick = this.movement.advance(frameTime, dtMs, this.snapshotClock);
    const trajectory = localTick === null ? null : this.movement.sample(localTick);
    const trajectoryPrevious = trajectory && Number.isFinite(this.previousLocalTick) ? this.movement.sample(this.previousLocalTick, false) : null;
    this.previousLocalTick = localTick ?? NaN;
    const elapsed = trajectory ? mix(trajectory.lo.elapsed, trajectory.hi.elapsed, trajectory.frac) : remote.elapsed;
    const platforms = getPlatforms(level, { ...authority, elapsed });
    const platformsRemote = trajectory ? getPlatforms(level, { ...authority, elapsed: remote.elapsed }) : platforms;
    const placed = this.placer.place({ authority, localId: this.props.playerId, input: this.props.input.current, remote, trajectory, trajectoryPrevious,
      platforms, platformsRemote, gates: level.gates, dtMs });
    this.cornerTransitions = placed.cornerTransitions;
    this.cornerCorrection = placed.cornerCorrection;
    const players: VisualPlayer[] = authority.players.map(player => {
      const body = placed.players.get(player.id);
      if (!body) return { ...player, anticipation: 0, path: 'remote' as const, interpolated: { x: player.x, y: player.y }, offset: { x: 0, y: 0 } };
      return { ...player, x: body.x, y: body.y, vx: body.vx, vy: body.vy, grounded: body.grounded, supportId: body.supportId,
        anticipation: 0, path: body.path, interpolated: body.interpolated, offset: body.offset };
    });
    const crates = authority.crates.map(crate => {
      const body = placed.crates.get(crate.id);
      return body ? { ...crate, x: body.x, y: body.y, vx: body.vx, vy: body.vy, grounded: body.grounded } : { ...crate };
    });
    const decorated = this.decorate(players, platforms, performance.now());
    const world: WorldState = { ...authority, players: decorated, crates };
    this.visualPlayers = decorated;
    this.visualPlatforms = platforms;
    return { level, world, remote, trajectory, placed, platforms, players: decorated, crates };
  }

  private readonly frame = (frameTime: number): void => {
    if (this.destroyed) return;
    this.raf = requestAnimationFrame(this.frame);
    const dtMs = this.lastFrameAt ? clamp(frameTime - this.lastFrameAt, 1, 50) : TICK_MS;
    const dt = dtMs / 1000;
    this.lastFrameAt = frameTime;
    if (!this.inView || document.hidden || this.cssWidth === 0 || this.cssHeight === 0) return;
    if (this.mode === 'hero' && this.props.reducedMotion && this.staticHeroDrawn) return;
    const begin = performance.now();
    const now = begin;
    // Read-only transport lifecycle is used solely to invalidate a replay during
    // B5's paused/reconnecting state. It is never a source of physical positions.
    const transport = (window as Window & { __hearthhop?: { playerId?: string | null; status?: string; inputPaused?: boolean } }).__hearthhop;
    if (transport?.playerId === this.props.playerId && (transport?.inputPaused || transport?.status !== 'online')) {
      this.movement.suspend(this.props.input.current); this.cooperation.reset();
    } else this.movement.observe(this.props.input.current, now);
    if (Math.min(2, Math.max(1, window.devicePixelRatio || 1)) !== this.dpr) this.resize();
    const ctx = this.ctx;
    const level = this.lastSnapshot?.level;
    // The motion clocks advance on the vsync-aligned frame timestamp, so a steady 60 fps
    // draws exactly uniform travel; effects and event playout use the wall clock.
    this.scene = this.mode === 'game' && level && this.authority ? this.sampleScene(level, frameTime, dtMs) : null;
    const theme = this.mode === 'hero' ? 'grove' : level?.theme ?? 'grove';
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = PALETTES[theme].sky;
    ctx.fillRect(0, 0, this.cssWidth, this.cssHeight);
    ctx.save();
    if (this.mode === 'hero') {
      // Art-directed crop: fills both the shell's postcard and full-width hero layouts.
      const scale = Math.max(this.cssWidth / 920, this.cssHeight / 535);
      this.worldScale = scale;
      ctx.translate(this.cssWidth - WORLD_W * scale, this.cssHeight - WORLD_H * scale);
      ctx.scale(scale, scale);
    } else {
      // Persistent HUD chips are laid out before the cooperative inset and the callouts so both avoid them.
      const beat = this.resetBeat && now - this.resetBeat.at < RESET_BEAT_MS ? this.resetBeat : null;
      const fall = beat?.attribution.kind === 'fall' && beat.attribution.point ? beat.attribution.point : null;
      this.cameraView = this.camera.sample(this.cssWidth, this.cssHeight, this.scene?.players ?? [], this.props.playerId,
        level ?? null, this.scene ? this.props.view : 'overview', dt, !!this.props.reducedMotion,
        this.scene ? { world: this.scene.world, platforms: this.scene.platforms } : null,
        fall ? { x: fall.x - 44, y: fall.y - 34, w: 88, h: 60 } : null);
      this.resetChip = beat && this.scene ? measureResetChip(ctx, this.cameraView, beat) : null;
      this.retriesChip = this.scene ? this.annotations.hudRect(ctx, this.cameraView, this.retriesLabel(this.scene.world)) : null;
      const avoid = [this.retriesChip, this.resetChip].filter((rect): rect is Rect => !!rect);
      if (this.scene) this.cameraView = this.cooperation.apply(this.cameraView, this.scene.players, this.scene.world, this.scene.level, this.props.playerId, now, !!this.props.reducedMotion, avoid);
      const view = this.cameraView;
      this.worldScale = view.scale;
      ctx.beginPath();
      ctx.rect(0, 0, view.width, view.height);
      ctx.clip();
      ctx.translate(view.offsetX - view.x * view.scale, view.offsetY - view.y * view.scale);
      ctx.scale(view.scale, view.scale);
    }
    ctx.beginPath();
    ctx.rect(0, 0, WORLD_W, WORLD_H);
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.backdrop(theme), 0, 0, WORLD_W, WORLD_H);
    if (this.mode === 'hero') {
      drawHero(ctx, this.props.reducedMotion ? 0 : now / 1000, !!this.props.reducedMotion);
      this.staticHeroDrawn = true;
    } else if (this.scene) {
      this.drawGame(this.scene, now);
    } else {
      this.drawWaiting();
    }
    ctx.restore();
    if (this.mode === 'game' && this.scene && this.cameraView) {
      this.drawKeyFlightsScreen(now, this.cameraView);
      const reserved = [...this.cooperation.reserved(), ...(this.resetChip ? [this.resetChip as Rect] : [])];
      this.annotations.draw(ctx, this.scene.level, this.scene.world, this.scene.players, this.cameraView, this.props.playerId, now, !!this.props.reducedMotion,
        reserved, this.cooperation.chips(this.props.playerId, now, !!this.props.reducedMotion), this.retriesLabel(this.scene.world),
        !!this.props.showHints);
      this.cooperation.drawInset(ctx, this.scene.level, this.scene.world, this.scene.players, this.props.playerId, now, !!this.props.reducedMotion);
      if (this.resetBeat && this.resetChip) drawResetChip(ctx, this.resetChip, this.resetBeat, now, !!this.props.reducedMotion);
    }
    this.drawCount++;
    this.fpsFrames++;
    if (!this.fpsAt) this.fpsAt = now;
    if (now - this.fpsAt >= 1000) {
      this.fps = Math.round(this.fpsFrames * 1000 / (now - this.fpsAt));
      this.fpsFrames = 0;
      this.fpsAt = now;
    }
    this.renderMs = mix(this.renderMs, performance.now() - begin, 0.08);
  };

  private drawWaiting(): void {
    const ctx = this.ctx;
    drawPlatform(ctx, { id: 'waiting-bank', x: 0, y: 555, w: 1200, h: 120, kind: 'ground' }, PALETTES.grove);
    // No invented players, switch states, or claims about a live session.
    pill(ctx, 'Waiting for the trail…', 600, 338, { size: 16, height: 37, padding: 33, fill: PAPER });
    spark(ctx, 600, 294, 10, AMBER);
  }

  /** The authoritative counter. `world.deaths` is carried across trails by the service
   * (a room total, like the result card's "Room retries"), so the chip says so. */
  private retriesLabel(world: WorldState): { id: string; label: string } {
    return { id: 'hud:retries', label: `ROOM RETRIES · ${world.deaths}` };
  }

  private drawGame(scene: RenderScene, now: number): void {
    const ctx = this.ctx;
    const { level, world, platforms, players, crates } = scene;
    const reducedMotion = !!this.props.reducedMotion;
    const time = reducedMotion ? 0 : now / 1000;
    const p = PALETTES[level.theme];
    this.consumeEvents(level, world, now);

    for (const hazard of level.hazards) {
      const state = describeHazard(hazard, level, world);
      drawHazard(ctx, hazard, state.active, state.warning, p, time, reducedMotion, state.progress, state.held, false, state.keyLocked);
    }

    for (const platform of platforms) drawPlatform(ctx, platform, p);
    for (const platform of level.platforms) {
      if (platform.switchIds?.length && !platforms.some(current => current.id === platform.id)) drawDormantBridge(ctx, platform);
    }
    drawCircuits(ctx, level, world, time, reducedMotion, false);
    drawProtectionProjectors(ctx, level, world);
    // The causal line of a received route change: a pulse from the lit plate(s) to the
    // gate/bridge/crossing they changed, along the very link drawn above.
    this.cooperation.drawPulse(ctx, now, reducedMotion);
    for (const gate of level.gates) drawGate(ctx, gate, isGateOpen(gate, world), p, this.cooperation.opening(gate.id, now, reducedMotion));

    const connected = world.players.filter(player => player.connected);
    const entered = connected.filter(player => player.entered);
    const allKeys = level.keys.every(key => world.collectedKeys.includes(key.id));
    drawDoor(ctx, level.exit, {
      unlocked: allKeys, waiting: connected.length - entered.length, total: connected.length,
      enteredColors: entered.map(player => LANTERN_COLORS[player.color % 8]), palette: p, time, reducedMotion,
      showLabel: false,
    });
    level.switches.forEach((plate, index) => drawSwitch(ctx, plate, world.switches.find(s => s.id === plate.id), index, p, false));
    // Where the courier fell (splash rings / singe / dust), under the bodies returning to spawn.
    const beat = this.resetBeat;
    const beatAge = beat ? now - beat.at : Infinity;
    if (beat && beatAge < RESET_BEAT_MS) drawFallMark(ctx, beat, now, reducedMotion);
    // The reset party fades in at spawn over 250 ms instead of snapping (still under reduced motion).
    const fade = !reducedMotion && beatAge < RESET_FADE_MS ? clamp(beatAge / RESET_FADE_MS, 0, 1) ** 0.8 : 1;
    ctx.save();
    ctx.globalAlpha *= fade;
    for (const crate of crates) drawCrate(ctx, crate);

    this.drawParticles(now, 'dust');
    for (const player of [...players].sort((a, b) => b.y - a.y || a.x - b.x)) {
      if (player.entered) {
        const since = now - (this.arrivals.get(player.id) ?? -Infinity);
        if (!reducedMotion && since >= 0 && since < 320) {
          ctx.save();
          ctx.globalAlpha *= (1 - since / 320) * 0.75;
          this.drawPlayer({ ...player, y: player.y - since / 90 }, players, platforms, now, true);
          ctx.restore();
        }
        continue;
      }
      this.drawPlayer(player, players, platforms, now);
    }
    ctx.restore();
    // The actual entered players gather as small non-colliding silhouettes inside the doorway.
    if (entered.length) this.drawArrivals(level, entered, now);
    this.drawEffects(now);
    if (!reducedMotion && now - this.resetAt < 260) {
      ctx.fillStyle = `rgba(255,247,222,${0.14 * Math.max(0, 1 - (now - this.resetAt) / 260)})`;
      ctx.fillRect(0, 0, WORLD_W, WORLD_H);
    }
  }

  private drawPlayer(
    player: VisualPlayer, players: VisualPlayer[], platforms: Platform[], now: number, entering = false,
  ): void {
    const ctx = this.ctx;
    const reducedMotion = !!this.props.reducedMotion;
    const local = player.id === this.props.playerId;
    const foot = player.y + PLAYER_H;
    if (player.connected && !entering) {
      const supportPlayer = players.find(p => p.id === player.supportId);
      const shadow = player.grounded && !supportPlayer ? { y: foot, distance: 0 } : platforms
        .filter(platform => platform.y >= foot && platform.y - foot < 155 && overlapX(player, platform))
        .sort((a, b) => a.y - b.y)
        .map(platform => ({ y: platform.y, distance: platform.y - foot }))[0];
      if (shadow) {
        ctx.beginPath();
        ctx.ellipse(player.x + 16, shadow.y + 1.4, 11.5 - shadow.distance * 0.025, 2.2, 0, 0, TAU);
        ctx.fillStyle = `rgba(35,49,36,${0.18 - shadow.distance * 0.0006})`;
        ctx.fill();
      }
    }
    const landingAge = now - (this.landings.get(player.id) ?? -Infinity);
    const landing = landingAge >= 0 && landingAge < 190 ? Math.sin(landingAge / 190 * Math.PI) : 0;
    let weightRoot: PlayerState = player;
    const visited = new Set<string>();
    while (weightRoot.supportId && !visited.has(weightRoot.id)) {
      visited.add(weightRoot.id);
      const below = players.find(candidate => candidate.id === weightRoot.supportId && candidate.connected && !candidate.entered);
      if (!below) break;
      weightRoot = below;
    }
    const braced = player.grounded && weightRoot.grounded && !!this.scene?.level.switches.some(plate =>
      Math.abs(weightRoot.y + PLAYER_H - plate.y - plate.h) < 2 && overlapX(weightRoot, plate));
    if (player.anticipation > 0 && player.grounded && !players.some(other => other.id === player.supportId)) {
      // A request pulse on the existing surface, not a premature landing/takeoff effect.
      const progress = reducedMotion ? 0.4 : clamp((now - (this.local?.jumpAt ?? now)) / 125, 0, 1);
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(player.x + 16, foot + 1.5, 14 + progress * 4, 2.6, 0, 0, TAU);
      ctx.strokeStyle = '#a66a24'; ctx.lineWidth = 3.6; ctx.stroke();
      ctx.strokeStyle = '#ffe288'; ctx.lineWidth = 2; ctx.stroke();
      ctx.restore();
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(player.x, player.y, PLAYER_W, PLAYER_H);
    ctx.clip();
    // The fallen courier's badge shakes briefly after a reset (never under reduced motion).
    const beat = this.resetBeat;
    const beatAge = beat ? now - beat.at : Infinity;
    const badgeShake = beat && !reducedMotion && beat.attribution.playerId === player.id && beatAge >= 0 && beatAge < RESET_SHAKE_MS
      ? Math.sin(beatAge / 24) * 2.6 * (1 - beatAge / RESET_SHAKE_MS) : 0;
    drawLantern(ctx, {
      x: player.x, y: player.y, color: player.color, number: (player.color % 8) + 1,
      grounded: player.grounded, facing: player.facing, speed: player.vx, vy: player.vy,
      time: reducedMotion ? 0 : now / 1000, phase: (hash(player.id) % 100) / 10,
      reducedMotion, local, disconnected: !player.connected, landing,
      gameplay: true, braced, badgeShake,
      carrying: players.some(other => other.id !== player.id && other.connected && !other.entered && overlapX(other, player) && Math.abs(other.y + PLAYER_H - player.y) < 0.75),
      anticipation: player.anticipation, celebration: now - this.celebrationAt < CELEBRATION_MS ? 1 : 0,
    });
    ctx.restore();
    const emote = this.emotes.get(player.id);
    if (emote && now - emote.at < 1800) {
      const message = emote.text === 'heart' ? '♥' : emote.text === 'here' ? 'HERE!' : 'HELLO!';
      pill(ctx, message, player.x + 16, player.y - (local ? 37 : 17), { size: 11, height: 22, fill: PAPER, stroke: '#9ca37e' });
    }
  }

  private drawArrivals(level: LevelDef, entered: PlayerState[], now: number): void {
    const ctx = this.ctx;
    const reducedMotion = !!this.props.reducedMotion;
    const age = now - this.celebrationAt;
    const celebrating = age >= 0 && age < CELEBRATION_MS;
    if (celebrating) {
      // The doorway is lit for the whole team: a warm bloom that settles as the hold ends.
      const cx = level.exit.x + level.exit.w / 2, cy = level.exit.y + level.exit.h * 0.55;
      const radius = level.exit.h * 1.05;
      const alpha = (reducedMotion ? 0.28 : 0.42) * (1 - clamp((age - 1500) / (CELEBRATION_MS - 1500), 0, 1)) * (reducedMotion ? 1 : 0.86 + Math.sin(age / 210) * 0.14);
      const glow = ctx.createRadialGradient(cx, cy, radius * 0.18, cx, cy, radius);
      glow.addColorStop(0, `rgba(255,236,170,${alpha})`);
      glow.addColorStop(0.55, `rgba(255,224,150,${alpha * 0.45})`);
      glow.addColorStop(1, 'rgba(255,224,150,0)');
      ctx.save();
      ctx.fillStyle = glow;
      ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
      ctx.restore();
    }
    const scale = Math.min(0.66, (level.exit.w - 23) / (entered.length * 34));
    const width = entered.length * 34 * scale;
    for (let i = 0; i < entered.length; i++) {
      const player = entered[i];
      // Little hops inside the doorway while the team celebrates; still with reduced motion.
      const hop = celebrating && !reducedMotion ? Math.max(0, Math.sin(age / 175 + i * 1.1)) * 7 * scale : 0;
      drawLantern(ctx, {
        x: level.exit.x + level.exit.w / 2 - width / 2 + i * 34 * scale,
        y: level.exit.y + level.exit.h - PLAYER_H * scale - 4 - hop,
        color: player.color, number: (player.color % 8) + 1, facing: i % 2 ? -1 : 1,
        grounded: true, speed: 0, vy: 0, scale,
        time: now / 1000, phase: i, reducedMotion, celebration: 1, gameplay: true,
      });
    }
  }

  private consumeEvents(level: LevelDef, world: WorldState, now: number): void {
    const pending: QueuedEvent[] = [];
    for (const item of this.events) {
      if (item.when > now) { pending.push(item); continue; }
      this.eventCount++;
      const event = item.event;
      if (now - item.when > 500) {
        playGameEvent(event, item.key, false);
        continue;
      }
      // The engine's reset is anonymous; the attributed outcome selects the cue (same key: one sound).
      const sounded = event.type === 'reset' && this.resetBeat?.eventId === event.id ? { ...event, text: this.resetBeat.attribution.kind } : event;
      playGameEvent(sounded, item.key, this.props.sound);
      const player = world.players.find(p => p.id === event.playerId);
      const x = event.x ?? (player ? player.x + PLAYER_W / 2 : level.exit.x + level.exit.w / 2);
      const y = event.y ?? (player ? player.y + PLAYER_H : level.exit.y + level.exit.h / 2);
      if (event.type === 'land' && event.playerId) this.landings.set(event.playerId, now);
      if (event.type === 'enter' && event.playerId) this.arrivals.set(event.playerId, now);
      if (event.type === 'emote' && event.playerId) this.emotes.set(event.playerId, { at: now, text: event.text ?? 'hello' });
      if (event.type === 'complete') this.celebrationAt = now;
      if (event.type === 'reset') this.resetAt = now;
      if (this.props.reducedMotion) continue;
      const rng = random(event.id * 1249 + hash(event.type));
      if (event.type === 'land') this.burst(x, y - 1, 5, 'dust', now, rng);
      if (event.type === 'jump') this.burst(x, y, 2, 'dust', now, rng);
      if (event.type === 'key') {
        this.burst(x, y, 13, 'glitter', now, rng);
        this.flights.push({ from: { x, y }, to: { x: level.exit.x + level.exit.w / 2, y: level.exit.y + 28 }, born: now, life: 900 });
      }
      if (event.type === 'switch') {
        const active = world.switches.find(plate => plate.id === event.targetId)?.active;
        this.rings.push({ x, y, born: now, life: 470, color: active ? '#e8bd61' : '#9aa77d' });
        if (active) this.burst(x, y, 5, 'glitter', now, rng);
      }
      if (event.type === 'enter') {
        this.rings.push({ x, y: y - 24, born: now, life: 430, color: AMBER });
        this.burst(x, y - 25, 7, 'confetti', now, rng);
      }
      if (event.type === 'complete') {
        // The whole-team payoff plays wherever the game view is: bursts are placed
        // inside the CURRENT frame (the follow view during the shell's hold, or the
        // whole trail in an overview/postcard), never at fixed off-screen sky points.
        const view = this.cameraView;
        const shown = view && view.follow
          ? { x: view.x, y: view.y, w: view.width / view.scale, h: view.height / view.scale }
          : { x: 0, y: 0, w: WORLD_W, h: WORLD_H };
        const door = { x: level.exit.x + level.exit.w / 2, y: level.exit.y };
        this.burst(door.x, door.y + 12, 35, 'confetti', now, rng);
        this.burst(door.x, door.y + level.exit.h * 0.5, 12, 'glitter', now + 60, rng);
        this.burst(shown.x + shown.w * 0.22, shown.y + shown.h * 0.16, 18, 'confetti', now + 100, rng);
        this.burst(shown.x + shown.w * 0.78, shown.y + shown.h * 0.14, 18, 'confetti', now + 180, rng);
        for (let i = 0; i < 5; i++) this.shower(shown.x + shown.w * (0.1 + i * 0.2), shown.y + 6, 8, now + 320 + i * 90, rng);
      }
    }
    this.events = pending;
    if (this.particles.length > 180) this.particles.splice(0, this.particles.length - 180);
  }

  /** Slow confetti drifting down from the top of the frame during the celebration hold. */
  private shower(x: number, y: number, count: number, now: number, rng: () => number): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: x + (rng() - 0.5) * 120, y: y + rng() * 10,
        vx: (rng() - 0.5) * 46, vy: 18 + rng() * 40, gravity: 52,
        size: 2.6 + rng() * 2.2, born: now + rng() * 260, life: 2000 + rng() * 700,
        color: ['#ecbb59', '#8fb395', '#e5ad96', '#d1c397', '#b8c6a2', '#f6dc9a'][i % 6],
        spin: (rng() - 0.5) * 7, kind: 'confetti',
      });
    }
  }

  private burst(x: number, y: number, count: number, kind: Particle['kind'], now: number, rng: () => number): void {
    for (let i = 0; i < count; i++) {
      const dust = kind === 'dust';
      const confetti = kind === 'confetti';
      this.particles.push({
        x: x + (rng() - 0.5) * (dust ? 15 : 10), y,
        vx: (rng() - 0.5) * (dust ? 64 : confetti ? 170 : 100),
        vy: -(dust ? 12 + rng() * 24 : confetti ? 75 + rng() * 150 : 35 + rng() * 90),
        gravity: dust ? 65 : confetti ? 180 : 120,
        size: dust ? 1.5 + rng() * 2 : confetti ? 2.4 + rng() * 2 : 1.5 + rng() * 2,
        born: now, life: dust ? 320 + rng() * 140 : confetti ? 1600 + rng() * 800 : 500 + rng() * 200,
        color: dust ? '#c1b48b' : confetti ? ['#ecbb59', '#8fb395', '#e5ad96', '#d1c397', '#b8c6a2'][i % 5] : i % 3 ? '#e3b14e' : '#fff2ba',
        spin: (rng() - 0.5) * 9, kind,
      });
    }
  }

  private drawParticles(now: number, kind?: Particle['kind']): void {
    const ctx = this.ctx;
    for (const particle of this.particles) {
      if (kind ? particle.kind !== kind : particle.kind === 'dust') continue;
      const elapsed = now - particle.born;
      if (elapsed < 0 || elapsed >= particle.life) continue;
      const t = elapsed / 1000;
      const life = elapsed / particle.life;
      const x = particle.x + particle.vx * t;
      const y = particle.y + particle.vy * t + particle.gravity * t * t / 2;
      ctx.save();
      ctx.globalAlpha = (particle.kind === 'dust' ? 0.42 : particle.kind === 'smoke' ? 0.34 : 0.9) * (1 - life);
      if (particle.kind === 'glitter' || particle.kind === 'ember') spark(ctx, x, y, particle.size * (1 - life * 0.4), particle.color);
      else if (particle.kind === 'dust') disc(ctx, x, y, particle.size * (1 + life * 0.8), particle.color);
      else if (particle.kind === 'smoke') disc(ctx, x, y, particle.size * (1 + life * 1.6), particle.color);
      else if (particle.kind === 'droplet') {
        ctx.beginPath();
        ctx.ellipse(x, y, particle.size * 0.7, particle.size * (1 + Math.min(1, Math.abs(particle.vy + particle.gravity * t) / 260)), 0, 0, TAU);
        ctx.fillStyle = particle.color;
        ctx.fill();
      } else {
        ctx.translate(x, y);
        ctx.rotate(t * particle.spin);
        ctx.scale(Math.cos(t * particle.spin) * 0.3 + 0.7, 1);
        rounded(ctx, -particle.size / 2, -particle.size, particle.size, particle.size * 1.8, 0.7);
        ctx.fillStyle = particle.color;
        ctx.fill();
      }
      ctx.restore();
    }
  }

  private drawEffects(now: number): void {
    const ctx = this.ctx;
    this.particles = this.particles.filter(p => now - p.born < p.life);
    this.rings = this.rings.filter(r => now - r.born < r.life);
    this.flights = this.flights.filter(f => now - f.born < f.life);
    for (const ring of this.rings) {
      const t = (now - ring.born) / ring.life;
      if (t < 0) continue;
      ctx.save();
      ctx.globalAlpha = (1 - t) * 0.65;
      ctx.beginPath();
      ctx.ellipse(ring.x, ring.y, 7 + t * 24, 4 + t * 14, 0, 0, TAU);
      ctx.strokeStyle = ring.color;
      ctx.lineWidth = 2 * (1 - t) + 0.5;
      ctx.stroke();
      ctx.restore();
    }
    for (const flight of this.flights) {
      const t = clamp((now - flight.born) / flight.life, 0, 1);
      const curve = (at: number): Point => ({
        x: mix(flight.from.x, flight.to.x, at),
        y: mix(flight.from.y, flight.to.y, at) - Math.sin(at * Math.PI) * 83,
      });
      const position = curve(t);
      ctx.save();
      ctx.globalAlpha = Math.min(1, (1 - t) * 6);
      for (let i = 1; i < 6; i++) {
        const behind = curve(Math.max(0, t - i * 0.018));
        disc(ctx, behind.x, behind.y, 2 - i * 0.23, '#e7bd63');
      }
      ctx.restore();
    }
    this.drawParticles(now);
    for (const [id, at] of this.landings) if (now - at > 1000) this.landings.delete(id);
    for (const [id, emote] of this.emotes) if (now - emote.at > 2000) this.emotes.delete(id);
  }

  private drawKeyFlightsScreen(now: number, view: CameraView): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, view.width, view.height);
    ctx.clip();
    for (const flight of this.flights) {
      const t = clamp((now - flight.born) / flight.life, 0, 1);
      const worldPoint = { x: mix(flight.from.x, flight.to.x, t), y: mix(flight.from.y, flight.to.y, t) - Math.sin(t * Math.PI) * 83 };
      const point = toScreen(view, worldPoint);
      ctx.save();
      ctx.globalAlpha = Math.min(1, (1 - t) * 8);
      disc(ctx, point.x, point.y, 16, '#fff0bf');
      drawKey(ctx, point.x, point.y, 1.08, -0.3 + t * 0.8);
      ctx.restore();
    }
    ctx.restore();
  }

  private resetDiagnostics(now: number): RenderDiagnostics['reset'] {
    const beat = this.resetBeat;
    if (!beat) {
      return { active: false, ageMs: null, durationMs: RESET_BEAT_MS, eventId: null, kind: null, playerId: null, name: null, color: null, surface: null,
        point: null, hazardId: null, basis: null, ticksAhead: null, label: null, chip: null };
    }
    const a = beat.attribution;
    const age = now - beat.at;
    return { active: age < RESET_BEAT_MS, ageMs: age, durationMs: RESET_BEAT_MS, eventId: beat.eventId, kind: a.kind, playerId: a.playerId, name: a.name, color: a.color,
      surface: a.surface, point: a.point ? Object.freeze({ ...a.point }) : null, hazardId: a.hazardId, basis: a.basis, ticksAhead: a.ticksAhead, label: resetLabel(beat),
      chip: this.resetChip ? Object.freeze({ x: this.resetChip.x, y: this.resetChip.y, w: this.resetChip.w, h: this.resetChip.h }) : null };
  }

  getDiagnostics(): RenderDiagnostics {
    const world = this.authority;
    const level = this.lastSnapshot?.level;
    const local = this.visualPlayers.find(p => p.id === this.props.playerId);
    const authority = world?.players.find(p => p.id === this.props.playerId);
    const remote = this.remote.diagnostics();
    const movement = this.movement.diagnostics();
    const trajectory = this.scene?.trajectory ?? null;
    return Object.freeze({
      rendererId: this.id, mode: this.mode,
      scene: this.mode === 'hero' ? 'original illustration' : world ? 'authoritative session' : 'awaiting snapshot',
      frames: this.drawCount, fps: this.fps, renderMs: Math.round(this.renderMs * 100) / 100,
      tick: world?.tick ?? null, epoch: world?.epoch ?? null, level: world?.levelIndex ?? null,
      theme: this.mode === 'hero' ? 'grove' : level?.theme ?? 'grove',
      viewport: Object.freeze({ width: this.cssWidth, height: this.cssHeight, dpr: this.dpr, backingWidth: this.canvas.width, backingHeight: this.canvas.height, worldScale: this.worldScale }),
      world: Object.freeze({ width: WORLD_W, height: WORLD_H, playerWidth: PLAYER_W, playerHeight: PLAYER_H }),
      interpolation: Object.freeze({ delayMs: remote.delayMs, bufferedSnapshots: remote.bufferedSnapshots, amount: remote.amount, staleSnapshots: this.staleSnapshots, renderTick: remote.renderTick, holds: remote.holds }),
      localPrediction: Object.freeze({ x: (local?.x ?? 0) - (authority?.x ?? 0), y: (local?.y ?? 0) - (authority?.y ?? 0), inputSequence: this.props.input.current.seq, acknowledgedSequence: authority?.lastInput ?? 0, cosmeticOnly: !trajectory }),
      prediction: Object.freeze({ ...movement, ...this.placer.diagnostics() }),
      renderClock: Object.freeze({ authorityTick: world?.tick ?? null, estimatedTick: this.snapshotClock.ready ? this.snapshotClock.tickAt(performance.now()) : null,
        localTick: trajectory ? movement.renderTick : null, remoteTick: Number.isFinite(remote.renderTick) ? remote.renderTick : null,
        leadTicks: movement.leadTicks, delayTicks: remote.delayTicks, localRate: movement.clockRate, remoteRate: remote.rate,
        localResyncs: movement.clockResyncs, remoteResyncs: remote.resyncs, clockOffsetMs: this.snapshotClock.ready ? this.snapshotClock.offsetMs : null }),
      cooperation: Object.freeze(this.cooperation.diagnostics(performance.now())),
      players: Object.freeze(this.visualPlayers.map(player => {
        const actual = world?.players.find(p => p.id === player.id) ?? player;
        return Object.freeze({ id: player.id, number: (player.color % 8) + 1, x: player.x, y: player.y, authorityX: actual.x, authorityY: actual.y,
          interpolatedX: player.interpolated.x, interpolatedY: player.interpolated.y, offsetX: player.offset.x, offsetY: player.offset.y, path: player.path,
          grounded: player.grounded, supportId: player.supportId, entered: player.entered, connected: player.connected });
      })),
      platforms: Object.freeze(this.visualPlatforms.map(platform => Object.freeze({ id: platform.id, x: platform.x, y: platform.y, w: platform.w, h: platform.h }))),
      gates: Object.freeze(level && world ? level.gates.map(gate => Object.freeze({ id: gate.id, open: isGateOpen(gate, world) })) : []),
      hazards: Object.freeze(level && world ? level.hazards.map(hazard => { const state = describeHazard(hazard, level, world); return Object.freeze({ id: hazard.id, active: state.active, label: state.label, keyLocked: state.keyLocked, held: state.held, seconds: state.seconds }); }) : []),
      switches: Object.freeze(level && world ? level.switches.map(plate => { const state = world.switches.find(s => s.id === plate.id); return Object.freeze({ id: plate.id, count: state?.count ?? 0, required: state?.required ?? plate.required, active: state?.active ?? false }); }) : []),
      events: Object.freeze({ lastId: this.lastEventId, consumed: this.eventCount, queued: this.events.length, particles: this.particles.length, keyArcs: this.flights.length }),
      celebration: Object.freeze({ ageMs: Number.isFinite(this.celebrationAt) ? performance.now() - this.celebrationAt : null, active: performance.now() - this.celebrationAt < CELEBRATION_MS, durationMs: CELEBRATION_MS }),
      reset: Object.freeze(this.resetDiagnostics(performance.now())),
      retries: Object.freeze({ deaths: world?.deaths ?? null, label: world ? this.retriesLabel(world).label : null, chip: this.retriesChip ? Object.freeze({ ...this.retriesChip }) : null }),
      reducedMotion: !!this.props.reducedMotion, audio: getAudioDiagnostics(),
      camera: this.cameraView ? Object.freeze({ ...this.cameraView, teamIds: Object.freeze([...(this.cameraView.teamIds ?? [])]), teamBounds: this.cameraView.teamBounds ? Object.freeze({ ...this.cameraView.teamBounds }) : undefined }) : null,
      annotations: Object.freeze(this.annotations.records.map(record => Object.freeze({ ...record, rect: Object.freeze({ ...record.rect }), anchor: Object.freeze({ ...record.anchor }) }))),
      labels: Object.freeze({ ...this.annotations.stats }),
      trailMap: this.annotations.mapRect ? Object.freeze({ ...this.annotations.mapRect }) : null,
      sampling: Object.freeze({ clock: trajectory ? 'movement-replay' as const : 'shared' as const,
        fromTick: trajectory ? trajectory.lo.tick : this.scene?.remote.a.tick ?? 0, toTick: trajectory ? trajectory.hi.tick : this.scene?.remote.b.tick ?? 0,
        horizontalOffset: this.local?.offsetX ?? 0, cornerTransitions: this.cornerTransitions, cornerCorrection: this.cornerCorrection }),
      jumpFeedback: Object.freeze({ requestedAt: this.local && Number.isFinite(this.local.jumpAt) ? this.local.jumpAt : null,
        ageMs: this.local && Number.isFinite(this.local.jumpAt) ? performance.now() - this.local.jumpAt : null,
        strength: local?.anticipation ?? 0, inputAcknowledged: this.local?.jumpAckAt != null }),
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    window.removeEventListener('resize', this.resize);
    for (const type of INPUT_EVENTS) window.removeEventListener(type, this.observeInput);
    renderers.delete(this);
    this.events = [];
    this.remote.reset();
    this.authority = null;
    this.particles = [];
    this.background = null;
  }
}
